import { useId, useRef, useState } from 'react'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'
import {
  addRecipients, hasSeparator, splitPasted, MAX_RECIPIENTS, OWN_ADDRESS_ERROR,
  ownerNeedsAddress, ownerAddressError, normalizeOwnerAddress, OWNER_ADDRESS_HELP,
} from './emailRecipients'

/**
 * "Also send to" — a schedule's OTHER email recipients (spec 138 §3.7).
 *
 * Rendered by the create modal (`QueueReportModal`, on the SCHEDULE path only — an
 * ad-hoc run stays self-only) and by the schedule editor (`EditScheduleModal`). One
 * component, so the two can never offer different rules.
 *
 * Decision (Jordan, 2026-10-07): any address, with the figures. Each address gets its
 * OWN email with the report attached; the owner's copy still goes to the signed-in
 * account's verified address. The client checks mirror the server's
 * (`emailRecipients.js` ↔ cb-api `resolveExtraRecipients`), and the server decides.
 *
 * Props:
 *   owner       `{ status: 'loading'|'ready'|'unknown', address }` — where the owner's
 *               own copy goes. `ready` with a null address means the account has none.
 *   recipients  the list as it will be sent (lowercase, deduped, owner excluded)
 *   onChange    called with the next list
 *   supported   false on a report that cannot be attached: the field is replaced by a
 *               sentence, because other recipients only ever get the attached file
 *               (spec 200: a standard report, an accounting statement, or a specialized
 *               report cb-api's mailer lists — not "standard reports only" any more)
 *   idPrefix    keeps ids unique where two modals could mount
 *
 * Accessibility: a fieldset with a visible legend; a labelled input whose errors, count
 * and copy are tied to it with `aria-describedby`; Enter adds; every remove control is
 * a real button named for its address, and focus returns to the input after an add or
 * a removal so a keyboard user does not lose their place. Interactive text uses
 * `text-cb-accent-text` (8.96:1 on white); the input is `.input-rc`, whose border is
 * `--color-rc-control` (3.03:1, SC 1.4.11).
 *
 * *review* — the input is NEVER disabled, not even at the cap (25 of 25 since spec 201). A
 * disabled input drops out of the tab order and cannot take focus, so removing one address
 * from a full list (or adding the last with Enter) left focus on `<body>`, behind the modal
 * (SC 2.4.3). At the cap it stays focusable, its describedby says "remove one to add
 * another", and one more is refused with the server's own sentence. For the same reason Add moves
 * focus back to the input: the button disables itself once the box is empty.
 *
 * Every address on screen carries `PENDO_IGNORE` (spec 094): these are third parties'
 * addresses, and Pendo's agent captures the text of what is clicked.
 *
 * Spec 139 §2.7 — when the owner is KNOWN to have no address (`owner` is `ready` with
 * none: on create the access-token claim is absent, on edit the row's `deliverTo` is),
 * the owner's line becomes a labelled input, "Your email address": the address their
 * own copy goes to, and the From cb-api passes so Centerbase will send to anyone at all.
 * It sits ABOVE the "Also send to" group, because it is not one of the other recipients.
 * The host owns the value and decides whether a save may go (`ownerAddressError`, the
 * client mirror of cb-api's `resolveOwnerAddress`); this component shows the problem —
 * on blur, or as soon as the host says a save was attempted — tied to the input with
 * `aria-describedby`. `loading` and `unknown` never show it (§2.8).
 *
 * *review* — and it is NEVER re-judged while the person types. The problem is a
 * `role="alert"` (assertive) that can echo the entry, so recomputing it per keystroke
 * after the first blur, or after a refused save, announced '"j" isn't…', '"jo" isn't…'
 * over the typing echo of a screen-reader user fixing the field. A change hides it; the
 * next blur or refused save judges it again, once — as the recipients input clears its
 * own error on change. Extra props:
 *   ownerAddress           the input's value
 *   onOwnerAddressChange   called with the next value
 *   ownerAddressAttempted  a count of saves the host refused for it (truthy once one was):
 *                          each new one shows the problem again, typing or not
 *   ownerAddressInputRef   the host focuses the input when it refuses a save
 *
 * *review* — the supplied address is the owner's OWN, so an address already listed
 * under "Also send to" that is then typed here is taken off the list on blur, with the
 * usual "That is your own address" sentence. Otherwise the list (and its count, and a
 * Link → Excel switch it caused) showed a recipient cb-api drops on save (`asOwner`).
 * A host that cannot attach (`supported` false) never shows it, and never sends
 * `deliverOwnerAddress`.
 */
const EmailRecipientsField = ({
  owner, recipients = [], onChange, supported = true, idPrefix = 'recipients',
  ownerAddress = '', onOwnerAddressChange, ownerAddressAttempted = false, ownerAddressInputRef,
}) => {
  const uid = useId()
  const base = `${idPrefix}-${uid.replace(/:/g, '')}`
  const inputId = `${base}-input`
  const errorId = `${base}-error`
  const countId = `${base}-count`
  const copyId = `${base}-copy`
  const ownerInputId = `${base}-owner-address`
  const ownerHelpId = `${base}-owner-help`
  const ownerErrorId = `${base}-owner-error`
  const inputRef = useRef(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState(null)
  const [ownerBlurred, setOwnerBlurred] = useState(false)
  // *review* — true from a change until the next blur or refused save: nothing is judged then.
  const [ownerEditing, setOwnerEditing] = useState(false)
  const [seenAttempt, setSeenAttempt] = useState(ownerAddressAttempted)
  if (ownerAddressAttempted !== seenAttempt) {
    // A NEW refused save (React's adjust-state-on-prop-change pattern, not an effect):
    // it is judged now, even if the person never left the field.
    setSeenAttempt(ownerAddressAttempted)
    if (ownerAddressAttempted) setOwnerEditing(false)
  }

  if (!supported) {
    return (
      <div className="mt-3" data-testid="recipients-unsupported">
        <span className="mb-1.5 block text-[11px] font-semibold text-rc-ink">Also send to</span>
        <p className="text-[11.5px] text-rc-muted">
          Other recipients need the report attached, and this report type can&apos;t be attached yet.
        </p>
      </div>
    )
  }

  const atCap = recipients.length >= MAX_RECIPIENTS
  // Spec 139: an owner with no address of their own supplies one here.
  const needsAddress = ownerNeedsAddress(owner)
  // The owner's own address, so adding it as an "other" recipient is refused with the
  // usual sentence — for an account with none, the one being supplied (cb-api drops it
  // from the list the same way, `asOwner`).
  const ownAddress = owner?.address || (needsAddress ? normalizeOwnerAddress(ownerAddress) || null : null)
  const ownerProblem = needsAddress ? ownerAddressError(ownerAddress) : null
  // Said on blur once something is typed, and at once when the host refused a save —
  // never while it is being typed (*review*, above).
  const showOwnerProblem = Boolean(ownerProblem) && !ownerEditing
    && (Boolean(ownerAddressAttempted) || (ownerBlurred && String(ownerAddress ?? '').trim() !== ''))

  const onOwnerBlur = () => {
    setOwnerBlurred(true)
    setOwnerEditing(false)
    // *review* — the owner's own address is not also an "other" recipient (cb-api drops
    // it on save, `asOwner`), so it comes off the list here, where the person can see it.
    const own = normalizeOwnerAddress(ownerAddress)
    if (own && !ownerProblem && recipients.includes(own)) {
      onChange?.(recipients.filter((r) => r !== own))
      setError(OWN_ADDRESS_ERROR)
    }
  }

  const add = (entries) => {
    const { recipients: next, error: problem, rejected } = addRecipients(recipients, entries, ownAddress)
    if (next.length !== recipients.length) onChange?.(next)
    setError(problem)
    // What could not be added stays in the box, so the person fixes it rather than retyping.
    setDraft(rejected.join(', '))
    inputRef.current?.focus()
  }

  const commitDraft = () => {
    const text = draft.trim()
    if (!text) return
    add(hasSeparator(text) ? splitPasted(text) : [text])
  }

  const onPaste = (e) => {
    const text = e.clipboardData?.getData('text') ?? ''
    // A single address pastes normally; a list is split for convenience (§3.7).
    if (!hasSeparator(text)) return
    e.preventDefault()
    // *review* — what was already typed is kept. The paste used to replace the box with
    // only the pasted entries, so an address typed first vanished without a word. The text
    // before and after the selection each stays its own entry, and the pasted list goes
    // in between: a typed address then a pasted list adds both, and anything that is not
    // an address stays in the box with the error, never silently dropped.
    const el = e.currentTarget
    const start = typeof el?.selectionStart === 'number' ? el.selectionStart : draft.length
    const end = typeof el?.selectionEnd === 'number' ? el.selectionEnd : draft.length
    add([...splitPasted(draft.slice(0, start)), ...splitPasted(text), ...splitPasted(draft.slice(end))])
  }

  const remove = (address) => {
    onChange?.(recipients.filter((r) => r !== address))
    setError(null)
    inputRef.current?.focus()
  }

  let ownerLine = null
  if (owner?.status === 'ready' && owner.address) {
    ownerLine = <>You — <span className={`${PENDO_IGNORE} font-semibold`}>{owner.address}</span></>
  } else if (needsAddress) {
    // Spec 139 replaces spec 138 deviation 1's sentence here ("…so no one below can be
    // emailed until your account has one"), which is no longer true: the owner supplies
    // the address in the input above, and that is what makes anyone emailable.
    ownerLine = null
  } else if (owner?.status === 'loading') {
    ownerLine = 'You — checking your address…'
  } else {
    // *review* — 'unknown' means the token could not be read, so this must not promise
    // a copy to an account that may have no address (useOwnDeliveryAddress.js).
    ownerLine = 'Your own copy goes to the address you sign in with, if your account has one.'
  }

  const describedBy = [error ? errorId : null, countId, copyId].filter(Boolean).join(' ')
  const ownerDescribedBy = [ownerHelpId, showOwnerProblem ? ownerErrorId : null].filter(Boolean).join(' ')

  return (
    <>
    {needsAddress && (
      // Spec 139 §2.7. Not inside "Also send to": this is the owner's OWN copy.
      <div className="mt-3 min-w-0" data-testid="owner-address-field">
        <label htmlFor={ownerInputId} className="mb-1 block text-[11px] font-semibold text-rc-ink">Your email address</label>
        <p id={ownerHelpId} className="mb-1.5 text-[11.5px] text-rc-muted">{OWNER_ADDRESS_HELP}</p>
        <input
          id={ownerInputId}
          ref={ownerAddressInputRef}
          type="text"
          inputMode="email"
          autoComplete="email"
          spellCheck={false}
          value={ownerAddress ?? ''}
          placeholder="name@example.com"
          aria-required="true"
          aria-invalid={showOwnerProblem ? true : undefined}
          aria-describedby={ownerDescribedBy}
          onChange={(e) => { setOwnerEditing(true); onOwnerAddressChange?.(e.target.value) }}
          onBlur={onOwnerBlur}
          className={`${PENDO_IGNORE} input-rc w-full`}
        />
        {showOwnerProblem && (
          // The problem can echo the entry back, which is the owner's address.
          <p id={ownerErrorId} role="alert" className={`${PENDO_IGNORE} mt-1 text-[11.5px] text-rc-danger-text`}>{ownerProblem}</p>
        )}
      </div>
    )}
    <fieldset className="mt-3 min-w-0" data-testid="email-recipients-field">
      <legend className="mb-1.5 block text-[11px] font-semibold text-rc-ink">Also send to</legend>
      {ownerLine && <p className="mb-2 text-[12px] text-rc-ink" data-testid="recipients-owner-line">{ownerLine}</p>}

      {recipients.length > 0 && (
        <ul aria-label="Other recipients" className={`${PENDO_IGNORE} mb-2 flex flex-col gap-1`}>
          {recipients.map((address) => (
            <li key={address} className="flex items-center justify-between gap-2 rounded-rc-control border border-rc-line px-2 py-1 text-[12px] text-rc-ink">
              <span className="min-w-0 break-all">{address}</span>
              <button
                type="button"
                onClick={() => remove(address)}
                aria-label={`Remove ${address}`}
                className="shrink-0 text-[12px] font-semibold text-cb-accent-text underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <label htmlFor={inputId} className="mb-1 block text-[11px] text-rc-muted">Add an email address</label>
      <div className="flex gap-2">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          value={draft}
          placeholder="name@example.com"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null) }}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitDraft() }
          }}
          className={`${PENDO_IGNORE} input-rc min-w-0 flex-1`}
        />
        <button
          type="button"
          onClick={commitDraft}
          disabled={!draft.trim()}
          className="btn-cb-toolbar shrink-0"
        >
          Add
        </button>
      </div>
      {error && (
        // The error can echo the entry back, which may be someone's address.
        <p id={errorId} role="alert" className={`${PENDO_IGNORE} mt-1 text-[11.5px] text-rc-danger-text`}>{error}</p>
      )}
      <p id={countId} className="mt-1 text-[11.5px] text-rc-muted" aria-live="polite">
        {recipients.length} of {MAX_RECIPIENTS}
        {atCap ? ' — remove one to add another.' : ''}
      </p>
      <p id={copyId} className="mt-0.5 text-[11.5px] text-rc-muted">
        Each address gets its own email with the report attached. It shows what you can see in
        Centerbase, not what they can.
      </p>
    </fieldset>
    </>
  )
}

export default EmailRecipientsField
